import * as React from "react";
import { bewertungsText, bewertungsTon } from "../../../data/coInternKatalog";

/**
 * Die Zellen der Liste „CO intern", die es nur dort gibt.
 *
 * Ausgelagert aus `ContactTableRedesign.tsx`: Die Komponente trägt bereits
 * 1300 Zeilen, und diese hier sind reine Darstellung ohne Zugriff auf ihren
 * Zustand. Zusammen mit `contactTableVarianten.ts` bleibt die Tabelle selbst
 * lesbar.
 */

/**
 * Ein Verweis, der den NAMEN zeigt statt der Adresse.
 *
 * Genau wie in der Lenkungsliste: Dort steht der Lieferantenname dreimal
 * nebeneinander — einmal schlicht, einmal als Verweis in den QM-Ordner, einmal
 * als Verweis auf die Website. Wer die Mappe kennt, sucht die Spalten so.
 * Unterstrichen, weil eine Farbe allein in einer Tabelle voller Chips kein
 * verlässliches Signal für „anklickbar" ist — und für Farbenblinde gar keins.
 *
 * `stopPropagation`: Ein Klick auf die Zeile öffnet sonst die Kontaktakte,
 * und der Verweis käme nie zum Zug.
 */
export const CoLink: React.FC<{ href?: string | null; text?: string | null; titel?: string }> = ({
  href,
  text,
  titel,
}) => {
  const beschriftung = (text ?? "").trim();
  const ziel = (href ?? "").trim();
  if (!beschriftung && !ziel) return null;
  if (!ziel) return <span>{beschriftung}</span>;
  return (
    <a
      className="co-link"
      href={ziel}
      target="_blank"
      rel="noopener noreferrer"
      title={titel ? `${titel}: ${ziel}` : ziel}
      onClick={(e) => e.stopPropagation()}
    >
      {beschriftung || ziel}
    </a>
  );
};

/**
 * Mehrzeiliger Text aus der Mappe (Leistung, Kündigungsfrist, Kriterien).
 *
 * Die Zeilenumbrüche der Quelle bleiben erhalten — „Arbeitssicherheit /
 * UVV-Prüfung / Internes Audit" sind drei Leistungen, keine Wortfolge. Die
 * Zelle deckelt die Höhe, den vollen Text gibt der Tooltip.
 */
export const CoMehrzeilig: React.FC<{ wert?: string | null }> = ({ wert }) => {
  const text = (wert ?? "").trim();
  if (!text) return null;
  return (
    <span className="co-mehrzeilig" title={text}>
      {text.split("\n").map((zeile, i) => (
        <span key={i} className="co-mehrzeilig-zeile">
          {zeile}
        </span>
      ))}
    </span>
  );
};

/**
 * Eine Schulnote als farbiges Feld.
 *
 * Ohne Note bleibt die Zelle LEER statt einen Strich zu zeigen: Die Reihe der
 * Halbjahre ist dreizehn Spalten breit, und ein Strich in jeder davon wäre
 * kein Hinweis mehr, sondern Rauschen. In der Mappe steht dort ebenfalls
 * nichts.
 */
export const CoNote: React.FC<{ wert?: string | null; titel?: string }> = ({ wert, titel }) => {
  const text = bewertungsText(wert);
  if (!text) return null;
  return (
    // Der volle Wert steht IMMER im Tooltip: In der Notenspalte steht nicht
    // zwingend eine Note — Zeile 54 der Mappe trägt dort die
    // Erstbewertungskriterien im Klartext. Die Zelle kürzt ihn mit Auslassung,
    // sonst zöge ein solcher Eintrag die ganze Spalte auseinander.
    <span
      className={`co-note ${bewertungsTon(wert)}`}
      title={titel ? `${titel}: ${text}` : text}
    >
      {text}
    </span>
  );
};

/**
 * Das Logo der Organisation — VOR ihrem Namen, ohne eigene Spalte.
 *
 * Ein Logo ist die Bildmarke derselben Organisation, deren Name daneben steht;
 * als eigene Spalte stünde dieselbe Auskunft zweimal nebeneinander. Vorn in
 * der Namensspalte findet das Auge die gesuchte Zeile dagegen schneller
 * wieder, als es den Namen lesen kann.
 *
 * Die Dateien liegen unter `public/co-logos/` und stammen aus dem Blatt
 * „Mitgliedschaften_Vereine" derselben Mappe. Schlägt das Laden fehl (Datei
 * umbenannt, Bestand aus einer anderen Umgebung), verschwindet das Bild
 * still — eine kaputte Bildmarke in jeder Zeile wäre schlimmer als keine.
 *
 * `alt=""`: Das Bild ist schmückend, weil der Name unmittelbar daneben steht.
 * Mit Beschriftung läse ein Screenreader jeden Lieferanten doppelt vor.
 */
export const CoLogo: React.FC<{ datei?: string | null }> = ({ datei }) => {
  const [fehler, setFehler] = React.useState(false);
  const d = (datei ?? "").trim();
  if (!d || fehler) return null;
  return (
    <img
      className="co-logo"
      src={`/co-logos/${d}`}
      alt=""
      loading="lazy"
      onError={() => setFehler(true)}
    />
  );
};

/**
 * Läuft die Beziehung noch?
 *
 * Dreiwertig: Ohne Eintrag in der Lenkungsliste bleibt die Zelle leer — „wir
 * wissen es nicht" ist etwas anderes als „beendet".
 */
export const CoAktiv: React.FC<{ aktiv?: boolean | null }> = ({ aktiv }) => {
  if (aktiv === null || aktiv === undefined) return null;
  return (
    <span className={`co-aktiv ${aktiv ? "co-aktiv-ja" : "co-aktiv-nein"}`}>
      {aktiv ? "aktiv" : "beendet"}
    </span>
  );
};
